/**
 * FikoTeklif — Anlık Teklif/Maliyet Motoru
 * STP/model → ağırlık + malzeme + işleme süresi + işlemler → fiyat teklifi.
 * Mesh hacmi (FikoMassInfo) + malzeme yoğunluğu/fiyatı + talaş kaldırma hızı (MRR) ile çalışır.
 */

/** Malzeme fiyat + işlenebilirlik (₺/kg ve cm³/dk talaş kaldırma hızı). Piyasaya göre düzenlenebilir. */
export interface MalzemePiyasa { fiyatKg: number; mrr: number; }

export const MALZEME_PIYASA: Record<string, MalzemePiyasa> = {
  'Alüminyum':       { fiyatKg: 145, mrr: 35 },
  'Çelik':           { fiyatKg: 42,  mrr: 12 },
  'Paslanmaz Çelik': { fiyatKg: 180, mrr: 7  },
  'Bakır':           { fiyatKg: 360, mrr: 18 },
  'Pirinç':          { fiyatKg: 340, mrr: 28 },
  'Bronz':           { fiyatKg: 420, mrr: 16 },
  'Titanyum':        { fiyatKg: 1900, mrr: 4 },
  'PLA Plastik':     { fiyatKg: 320, mrr: 50 },
  'ABS Plastik':     { fiyatKg: 280, mrr: 50 },
  'Naylon':          { fiyatKg: 260, mrr: 45 },
  'Cam':             { fiyatKg: 90,  mrr: 6  },
  'Ahşap (Meşe)':    { fiyatKg: 55,  mrr: 60 },
  'Özel':            { fiyatKg: 100, mrr: 25 },
};

export interface TeklifGirdi {
  netAgirlikKg: number;     // bitmiş parçanın ağırlığı (model hacmi × yoğunluk)
  brutAgirlikKg: number;    // satın alınan stok bloğu ağırlığı (bbox hacmi × yoğunluk)
  talasHacimCm3: number;    // kaldırılacak talaş (bbox - model)
  malzemeFiyatKg: number;   // ₺/kg
  islemeHizCm3Dk: number;   // talaş kaldırma hızı (MRR), cm³/dk
  saatUcreti: number;       // tezgâh+operatör ₺/saat
  kurulumDk: number;        // sabit kurulum/programlama süresi (dk), adet'e bölünür
  kaplamaUcret: number;     // yüzey işlem/kaplama (parça başı ₺)
  ekIslemUcret: number;     // ek işlem/lojistik (parça başı ₺)
  karYuzde: number;         // kâr marjı %
  kdvYuzde: number;         // KDV %
  adet: number;             // sipariş adedi
  fireYuzde: number;        // hammadde fire payı %
}

export interface TeklifSonuc {
  hammaddeMaliyet: number;  // birim hammadde
  islemeSureDk: number;     // birim işleme süresi
  islemeMaliyet: number;    // birim işleme
  kurulumBirim: number;     // birim başına düşen kurulum
  kaplamaMaliyet: number;
  ekMaliyet: number;
  birimMaliyet: number;     // toplam maliyet/adet (kârsız)
  birimKar: number;
  birimFiyat: number;       // KDV hariç satış/adet
  birimKdv: number;
  birimKdvli: number;       // KDV dahil satış/adet
  toplamFiyat: number;      // KDV hariç sipariş
  toplamKdvli: number;      // KDV dahil sipariş
}

/** Maliyet → fiyat hesabı. Tüm parametreler düzenlenebilir; tek merkezi formül. */
export function teklifHesapla(g: TeklifGirdi): TeklifSonuc {
  const adet = Math.max(1, g.adet || 1);
  // Hammadde: stok bloğu × fire payı (talaşlı imalatta blok satın alınır)
  const hammaddeKg = Math.max(g.brutAgirlikKg, g.netAgirlikKg) * (1 + (g.fireYuzde || 0) / 100);
  const hammaddeMaliyet = hammaddeKg * g.malzemeFiyatKg;
  // İşleme: talaş hacmi / MRR + %30 yaklaşma/temizlik payı
  const kabaDk = g.islemeHizCm3Dk > 0 ? g.talasHacimCm3 / g.islemeHizCm3Dk : 0;
  const islemeSureDk = kabaDk * 1.3;
  const islemeMaliyet = (islemeSureDk / 60) * g.saatUcreti;
  // Kurulum: sabit süre adede bölünür (tek parçada pahalı, seri üretimde ucuz)
  const kurulumBirim = ((g.kurulumDk / 60) * g.saatUcreti) / adet;
  const kaplamaMaliyet = g.kaplamaUcret || 0;
  const ekMaliyet = g.ekIslemUcret || 0;
  const birimMaliyet = hammaddeMaliyet + islemeMaliyet + kurulumBirim + kaplamaMaliyet + ekMaliyet;
  const birimKar = birimMaliyet * (g.karYuzde || 0) / 100;
  const birimFiyat = birimMaliyet + birimKar;
  const birimKdv = birimFiyat * (g.kdvYuzde || 0) / 100;
  const birimKdvli = birimFiyat + birimKdv;
  return {
    hammaddeMaliyet, islemeSureDk, islemeMaliyet, kurulumBirim, kaplamaMaliyet, ekMaliyet,
    birimMaliyet, birimKar, birimFiyat, birimKdv, birimKdvli,
    toplamFiyat: birimFiyat * adet, toplamKdvli: birimKdvli * adet,
  };
}
